import { useCallback, useMemo, useRef, useState } from 'react';

import type { TurnstileWidgetHandle } from '@login/components/turnstile-widget';

/**
 * Owns the Cloudflare Turnstile token for a form.
 *
 * The callbacks are memoised so `<TurnstileWidget>` does not receive new
 * `onSuccess`/`onExpire`/`onError` props on every parent render.
 */
export const useTurnstile = (enabled = false) => {
	const [token, setToken] = useState('');
	const ref = useRef<TurnstileWidgetHandle>(null);

	const clearToken = useCallback(() => setToken(''), []);

	const reset = useCallback(() => {
		ref.current?.reset();
		setToken('');
	}, []);

	/** True while the challenge is shown but not yet solved. */
	const isPending = enabled && !token;

	return useMemo(
		() => ({
			ref,
			token,
			isPending,
			reset,
			onSuccess: setToken,
			onExpire: clearToken,
			onError: clearToken,
		}),
		[token, isPending, reset, clearToken]
	);
};

export default useTurnstile;
